import { useMemo, useState } from 'react';
import { MessageSquare, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import type { Task } from '../../lib/tasksApi';
import { useCreateTask, useDeleteTask, useUpdateTaskById } from '../../hooks/useTasks';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { PRIORITY_BADGE } from './taskConstants';
import { LabelPill } from './LabelPill';
import { ProjectSelect } from './ProjectSelect';
import { useI18n } from '../../contexts/I18nContext';
import { formatEstimate } from '../../lib/estimate';
import { Popover } from './linear/Popover';
import { useToast } from './linear/Toast';
import { useConfirm } from '../ui/ConfirmProvider';

interface KanbanBoardProps {
  tasks: Task[];
  onCardClick: (id: string) => void;
  // CLE-167: quick-add must create the task inside a project. When the board is
  // scoped to one project (via the page's project filter) we pass it here so the
  // inline quick-add inherits it. When null the board is cross-project and
  // quick-add is disabled with a hint to pick a project first.
  projectId?: string | null;
  // CLE-197: the project name only helps on a cross-project board; when the
  // scope is a single project (filter or a project's own Tasks tab) it's noise.
  showProjectName?: boolean;
}

function formatHours(hours: number): string {
  if (!hours) return '0h';
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

function KanbanCard({
  task,
  onClick,
  onDragStart,
  showProjectName = false,
}: {
  task: Task;
  onClick: () => void;
  onDragStart: (e: React.DragEvent) => void;
  showProjectName?: boolean;
}) {
  const { t } = useI18n();
  const del = useDeleteTask();
  const toast = useToast();
  const confirm = useConfirm();

  async function handleDelete(close: () => void) {
    close();
    if (!(await confirm({ message: t('tasks.row.confirmDelete'), danger: true }))) return;
    del.mutate(task.id, {
      onSuccess: () => toast.show(t('tasks.row.deleted')),
      onError: () => toast.show(t('tasks.row.deleteFailed')),
    });
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="group/card cursor-pointer rounded-md border border-gray-200 bg-white p-3 shadow-sm transition hover:border-brand-300 hover:shadow"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] text-gray-400">{task.identifier ?? '—'}</span>
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${PRIORITY_BADGE[task.priority]}`}
          >
            {task.priority}
          </span>
          <Popover
            width={160}
            align="end"
            trigger={({ open, toggle, ref }) => (
              <button
                type="button"
                ref={ref}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle();
                }}
                aria-label={t('tasks.row.menu')}
                className={`flex h-5 w-5 items-center justify-center rounded text-gray-400 transition-opacity hover:bg-gray-100 hover:text-gray-700 ${
                  open ? 'opacity-100' : 'opacity-0 group-hover/card:opacity-100'
                }`}
              >
                <MoreHorizontal size={14} />
              </button>
            )}
          >
            {(close) => (
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => handleDelete(close)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={13} /> {t('tasks.row.delete')}
                </button>
              </div>
            )}
          </Popover>
        </div>
      </div>
      <p className="mt-1 text-sm font-medium text-gray-900">{task.title}</p>
      {showProjectName && task.projectName && (
        // CLE-167: show the parent project so a cross-project board stays
        // legible — light twin of the chip used in the list view.
        <span className="mt-1 inline-block max-w-full truncate rounded-full border border-gray-200 bg-gray-50 px-2 py-px text-[11px] font-medium text-gray-600">
          {task.projectName}
        </span>
      )}
      {task.labels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {task.labels.map((l) => (
            <LabelPill key={l.id} label={l} />
          ))}
        </div>
      )}
      <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
        {task.estimate != null && <span>{formatEstimate(task.estimate)}</span>}
        {task.subIssueCount > 0 && (
          <span>
            {task.subIssueDoneCount}/{task.subIssueCount}
          </span>
        )}
        {task.commentCount > 0 && (
          <span className="inline-flex items-center gap-0.5">
            <MessageSquare size={11} /> {task.commentCount}
          </span>
        )}
        {task.assignee && <span className="ml-auto truncate">{task.assignee.name}</span>}
      </div>
    </div>
  );
}

/**
 * Drag-and-drop Kanban board (FR-TASK #6). Columns are the configurable
 * statuses (CLE-DASH): workflow order without CANCELLED, keyed by status id.
 * Uses native HTML5 DnD (no extra deps). Dropping a card on a column patches
 * its status; each column shows a task count + summed estimate, and supports
 * quick-add.
 */
export function KanbanBoard({
  tasks,
  onCardClick,
  projectId = null,
  showProjectName = false,
}: KanbanBoardProps) {
  const { t } = useI18n();
  const { boardColumns } = useTaskStatuses();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStatus, setOverStatus] = useState<string | null>(null);
  const [quickAddCol, setQuickAddCol] = useState<string | null>(null);
  const [quickTitle, setQuickTitle] = useState('');
  // CLE-199: on a cross-project board the quick-add must pick a project inline
  // before a task can be created. Holds the chosen project for the open row.
  const [quickProjectId, setQuickProjectId] = useState('');

  const update = useUpdateTaskById();
  const create = useCreateTask();

  const byStatus = useMemo(() => {
    const map = new Map<string, Task[]>();
    for (const col of boardColumns) map.set(col.id, []);
    for (const task of tasks) {
      const list = map.get(task.statusId);
      if (list) list.push(task);
    }
    return map;
  }, [tasks, boardColumns]);

  function handleDrop(statusId: string) {
    setOverStatus(null);
    if (!dragId) return;
    const task = tasks.find((t) => t.id === dragId);
    setDragId(null);
    if (!task || task.statusId === statusId) return;
    update.mutate({ id: task.id, statusId });
  }

  // The project a quick-add task lands in: the board's locked project wins,
  // otherwise the inline-picked one (CLE-199 cross-project quick-add).
  const effectiveQuickProjectId = projectId ?? quickProjectId;

  function closeQuickAdd() {
    setQuickAddCol(null);
    setQuickTitle('');
    if (!projectId) setQuickProjectId('');
  }

  function submitQuickAdd(statusId: string) {
    const title = quickTitle.trim();
    // CLE-167/199: a task cannot be created without a project. On a scoped
    // board projectId is set; on a cross-project board the user must first
    // pick a project inline — keep the row open until both are present.
    if (!effectiveQuickProjectId) return;
    if (!title) {
      closeQuickAdd();
      return;
    }
    create.mutate(
      { title, statusId, projectId: effectiveQuickProjectId },
      {
        onSuccess: () => {
          setQuickTitle('');
          setQuickAddCol(null);
        },
      },
    );
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {boardColumns.map((col) => {
        const colTasks = byStatus.get(col.id) ?? [];
        const colHours = colTasks.reduce((sum, task) => sum + (task.estimate ?? 0), 0);
        const isOver = overStatus === col.id;
        return (
          <div
            key={col.id}
            onDragOver={(e) => {
              e.preventDefault();
              setOverStatus(col.id);
            }}
            onDragLeave={() => setOverStatus((s) => (s === col.id ? null : s))}
            onDrop={() => handleDrop(col.id)}
            className={`flex w-72 shrink-0 flex-col rounded-lg border bg-gray-50 ${
              isOver ? 'border-brand-400 ring-2 ring-brand-200' : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between px-3 py-2">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: col.color }}
                  aria-hidden="true"
                />
                {col.name}
              </span>
              <span className="text-xs text-gray-500">
                {colTasks.length} · {formatHours(colHours)}
              </span>
            </div>

            <div className="flex flex-1 flex-col gap-2 px-2 pb-2">
              {colTasks.map((task) => (
                <KanbanCard
                  key={task.id}
                  task={task}
                  showProjectName={showProjectName}
                  onClick={() => onCardClick(task.id)}
                  onDragStart={(e) => {
                    setDragId(task.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                />
              ))}

              {quickAddCol === col.id ? (
                // CLE-199: when the board is cross-project, show an inline
                // project picker before the title input so the task can still
                // be created without leaving the board.
                <div className="flex flex-col gap-1.5">
                  {!projectId && (
                    <ProjectSelect
                      value={quickProjectId}
                      onChange={setQuickProjectId}
                      compact
                      width={260}
                      placeholder={t('tasks.quickAdd.pickProject')}
                    />
                  )}
                  <input
                    autoFocus={Boolean(projectId)}
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    onBlur={() => {
                      // Don't submit on blur when we still need a project — the
                      // picker popover steals focus; only auto-close empty rows.
                      if (effectiveQuickProjectId) submitQuickAdd(col.id);
                      else if (!quickTitle.trim()) closeQuickAdd();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitQuickAdd(col.id);
                      if (e.key === 'Escape') closeQuickAdd();
                    }}
                    placeholder={
                      !effectiveQuickProjectId
                        ? t('tasks.quickAdd.pickProjectFirst')
                        : t('tasks.quickAdd.titlePlaceholder')
                    }
                    className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setQuickAddCol(col.id);
                    setQuickTitle('');
                    if (!projectId) setQuickProjectId('');
                  }}
                  className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <Plus size={12} /> {t('tasks.addToColumn')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
